import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { queueIdInSavingRemarkOptionCaptures, RemarkOptionType } from '../../../mappings';
import { useSaveCapture } from '../hooks/useSaveCapture';
import { AddRemarkOptionCapturesModal } from '../modal/AddRemarkOptionCapturesModal';
import { FieldUpdate, getImagePathByCaptureId, SiRequestUser, urlToFile } from '../utils';
import { schema } from './Schema';

enum MapOptionType {
  EXISTED = 'EXISTED',
  NEW = 'NEW',
}

type RemarkOptionCapturesValue = {
  url: string;
  capture_id: string;
  capture: string | File; // string for view, File for edit
};

interface RemarkOptionWithCaptures {
  key: {
    optionType: MapOptionType;
    optionValue: string;
  };
  value: RemarkOptionCapturesValue[];
}

interface RemarkOptionWithLabelAndCaptures extends RemarkOptionWithCaptures {
  label: string;
}

interface BasicRequestRemarkOption {
  id?: string;
  remarkOptionId: string;
}

interface TempRequestRemarkOption extends BasicRequestRemarkOption {
  captures: (string | RemarkOptionCapturesValue)[]; // string until edit image
}

interface RequestRemarkOption extends BasicRequestRemarkOption {
  captureIds: string[];
}

interface Props {
  data: any;
  requestData: any;
  isSiteSaving: boolean;
  captures: any[];
  refetchList: () => void;
  handleDirtyChange: (isDirty: boolean) => void;
  fieldUpdates: FieldUpdate[];
  setSelectedImagesData: React.Dispatch<
    React.SetStateAction<{ label: string; captures: { url: string; image: string | File }[] }>
  >;
  setShowImageModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const compareStringArraysDisregardingOrder = (arr1: string[], arr2: string[]): boolean => {
  if (!arr1 || !arr2) return arr1 === arr2;
  if (arr1.length !== arr2.length) return false;

  const countMap: Record<string, number> = {};

  for (const item of arr1) {
    countMap[item] = (countMap[item] || 0) + 1;
  }

  for (const item of arr2) {
    if (!countMap[item]) return false;
    countMap[item]--;
  }

  return Object.values(countMap).every((count) => count === 0);
};

const EditSiRemarks = forwardRef(
  (
    {
      data,
      requestData,
      isSiteSaving,
      captures,
      refetchList,
      handleDirtyChange,
      fieldUpdates,
      setSelectedImagesData,
      setShowImageModal,
    }: Props,
    ref
  ) => {
    const { currentUser, auth, refresh } = useAuth();
    const intl = useIntl();
    const { setToast } = useToast();
    const { saveCapture } = useSaveCapture();
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const isFirstRef = useRef(true);
    const isSiteUpdatedRef = useRef(false);
    const lastDefaultValuesRef = useRef<any>(undefined);
    const [showAddRemarkOptionCapturesModal, setShowAddRemarkOptionCapturesModal] = useState(false);
    const [selectedRemarkOptionCaptures, setSelectedRemarkOptionCaptures] =
      useState<RemarkOptionWithLabelAndCaptures>(undefined);
    const [remarkOptionCapturesDataMap, setRemarkOptionCapturesDataMap] = useState<
      RemarkOptionWithCaptures[]
    >([]);

    const remarkOptionIdToRequestRemarkOptionMap = useMemo(
      () =>
        new Map<string, RequestRemarkOption>(
          requestData.requestRemarkOptions.map((i: any) => [
            i.remark_option_id,
            {
              id: i._id,
              remarkOptionId: i.remark_option_id,
              captureIds: i.capture_ids,
            },
          ])
        ),
      [requestData.requestRemarkOptions]
    );

    const reviewerNames: SiRequestUser[] = requestData?.users;
    const getReviewerEmail = (userId?: string) =>
      reviewerNames?.find((reviewer) => reviewer.user_id === userId)?.email ?? userId;
    const getReviewerNames = (fieldUpdates: { updated_by: string }[]) =>
      Array.from(
        new Set(
          fieldUpdates
            .map((fieldUpdate) => getReviewerEmail(fieldUpdate.updated_by))
            .filter(Boolean)
        )
      );

    const getRemarkOptionReviewerNames = useCallback(
      (remarkOptionId: string) => {
        return getReviewerNames(
          fieldUpdates?.filter(
            (fieldUpdate) => fieldUpdate.path === `remark_options.[_id:${remarkOptionId}]`
          ) ?? []
        );
      },
      [fieldUpdates]
    );

    const { editSiRemarksList } = schema(intl);
    type FormDataType = z.infer<typeof editSiRemarksList>;

    const getRemarkLabel = useCallback(
      (option: any) => {
        const currentLang = option.translations?.find((t: any) => t.language === intl.locale);
        if (currentLang) {
          return currentLang.label;
        }
        const defaultLang = option.translations?.find((t: any) => t.language === 'en');
        if (defaultLang) {
          return defaultLang.label;
        }
        const anyLang = option.translations?.find((t: any) => t.label);
        if (anyLang) {
          return anyLang.label;
        }
        return '';
      },
      [intl.locale]
    );

    const getRemarkOptionCaptures = async (
      mapOptionType: MapOptionType,
      mapOptionValue: string,
      mapOptionCaptureIds: string[],
      isForView: boolean,
      skipToFileWhenCaptureIdExists: boolean
    ) => {
      const remarkOptionCaptures = captures?.filter((c) =>
        mapOptionCaptureIds?.includes(c.capture_id)
      );

      const value =
        remarkOptionCaptures?.length > 0
          ? await Promise.all(
              remarkOptionCaptures.map(async (c) => {
                const sourceUrl = c.url;
                const captureId = c.capture_id;
                const imageUrl = getImagePathByCaptureId(captureId, captures);
                return {
                  url: sourceUrl,
                  capture_id: captureId,
                  capture:
                    isForView || (skipToFileWhenCaptureIdExists && captureId)
                      ? imageUrl
                      : await urlToFile(imageUrl),
                };
              })
            )
          : [];

      return {
        key: {
          optionType: mapOptionType,
          optionValue: mapOptionValue,
        },
        value: value,
      };
    };

    const flattenRemarkOptionCaptures = (data: {
      key: { optionType: MapOptionType; optionValue: string };
      value: { capture_id: string; [key: string]: any }[];
    }) => {
      return {
        remarkOptionId: data.key.optionValue,
        remarkOptionType: data.key.optionType,
        remarkOptionCaptureIds: data.value.map((v) => v.capture_id),
      };
    };

    const dataAllRemarkOptions = useMemo(() => data.all ?? [], [data.all]);

    const remarksList: any[] = useMemo(() => {
      return dataAllRemarkOptions.filter((item: any) => item.type === RemarkOptionType.GENERAL);
    }, [dataAllRemarkOptions, getRemarkLabel]);

    const remarksListIds: string[] = useMemo(
      () => remarksList.map((item: any) => item.remark_option_id),
      [remarksList]
    );

    const allGeneralRemarkLabelsMap = useMemo(() => {
      const map = new Map<string, string>();

      for (const remark of remarksList) {
        const remarkLabel = getRemarkLabel(remark);
        if (!map.has(remarkLabel)) {
          map.set(remarkLabel, remark.remark_option_id);
        }
      }

      return map;
    }, [remarksList, getRemarkLabel]);

    const getGeneralRemarkOptionByLabel = useCallback(
      (label: string) => {
        return remarksList.find((option) => getRemarkLabel(option) === label);
      },
      [remarksList, getRemarkLabel]
    );

    const dataSelectedRemarkOptions: any[] = useMemo(() => data.selected ?? [], [data.selected]);

    const selectedRemarks: string[] = useMemo(
      () =>
        dataSelectedRemarkOptions
          .filter((item: any) => remarksListIds.includes(item.remark_option_id))
          .map((item: any) => item.remark_option_id),
      [dataSelectedRemarkOptions, remarksListIds]
    );

    const selectedOthers: { remark: string }[] = useMemo(
      () =>
        dataSelectedRemarkOptions
          .filter((item: any) => !remarksListIds.includes(item.remark_option_id))
          .map((item: any) => ({ remark: getRemarkLabel(item) })),
      [dataSelectedRemarkOptions, remarksListIds, getRemarkLabel]
    );

    const otherRemarksList: any[] = useMemo(
      () =>
        dataAllRemarkOptions.filter(
          (remark) =>
            remark.type === RemarkOptionType.SPECIFIC && remark.translations?.some((t) => t.label)
        ),
      [dataAllRemarkOptions, getRemarkLabel]
    );

    const getSpecificRemarkOptionByLabel = useCallback(
      (label: string) => {
        return otherRemarksList.find((option) => getRemarkLabel(option) === label);
      },
      [otherRemarksList, getRemarkLabel]
    );

    const defaultValues: FormDataType = useMemo(() => {
      return {
        remarks: [...selectedRemarks],
        others: selectedOthers.length > 0 ? [...selectedOthers] : [{ remark: '' }],
      };
    }, [selectedRemarks, selectedOthers]);

    const {
      handleSubmit,
      control,
      reset,
      setValue,
      getValues,
      formState: { defaultValues: currentDefaultValues, errors },
    } = useForm({
      resolver: zodResolver(editSiRemarksList),
      defaultValues,
    });
    const { fields, append, remove } = useFieldArray({
      control,
      name: 'others',
    });

    const currentRemarks = useWatch({ control, name: 'remarks' });
    const currentOthers = useWatch({ control, name: 'others' });

    const isRemarkOptionsDirty = useMemo(() => {
      const isRemarkChanged = !compareStringArraysDisregardingOrder(
        currentRemarks,
        currentDefaultValues.remarks
      );
      const isOtherChanged = !compareStringArraysDisregardingOrder(
        currentOthers.filter((item) => item.remark).map((item) => item.remark),
        currentDefaultValues.others.filter((item) => item.remark).map((item) => item.remark)
      );

      return isRemarkChanged || isOtherChanged;
    }, [currentRemarks, currentOthers, currentDefaultValues]);

    const isCapturesDirty = useMemo(() => {
      if (!remarkOptionCapturesDataMap.length) return false;
      return remarkOptionCapturesDataMap.some((item) => {
        const { remarkOptionId, remarkOptionType, remarkOptionCaptureIds } =
          flattenRemarkOptionCaptures(item);
        if (remarkOptionCaptureIds.some((captureId) => captureId === null)) return true;
        const existedOption = remarkOptionIdToRequestRemarkOptionMap.get(remarkOptionId);
        const dataRemarkOptionCaptureIds = existedOption?.captureIds ?? [];
        return !compareStringArraysDisregardingOrder(
          dataRemarkOptionCaptureIds,
          remarkOptionCaptureIds
        );
      });
    }, [remarkOptionCapturesDataMap, remarkOptionIdToRequestRemarkOptionMap]);

    const isDirty = useMemo(
      () => isRemarkOptionsDirty || isCapturesDirty,
      [isRemarkOptionsDirty, isCapturesDirty]
    );

    useEffect(() => {
      handleDirtyChange?.(isDirty);
    }, [isDirty]);

    const resetNewDefaultValues = (newValues: FormDataType) => {
      reset(newValues);
      lastDefaultValuesRef.current = newValues;
    };

    useEffect(() => {
      if (!data.all || !data.selected) return;

      const defaultValuesUnchanged =
        compareStringArraysDisregardingOrder(
          lastDefaultValuesRef.current?.remarks,
          defaultValues.remarks
        ) &&
        compareStringArraysDisregardingOrder(
          lastDefaultValuesRef.current?.others.map((item) => item.remark),
          defaultValues.others.map((item) => item.remark)
        );

      if (isFirstRef.current) {
        if (!defaultValuesUnchanged) {
          resetNewDefaultValues(defaultValues);
        }
        isFirstRef.current = false;
        return;
      }

      if (isSiteUpdatedRef.current && !isDirty) {
        if (!defaultValuesUnchanged) {
          resetNewDefaultValues(defaultValues);
        }
        isSiteUpdatedRef.current = false;
        return;
      }

      if (defaultValuesUnchanged) return;

      const currentRemarkValues = getValues('remarks');
      const currentOtherValues = getValues('others');

      resetNewDefaultValues(defaultValues);

      const filteredRemarks = currentRemarkValues.filter((remark: string) =>
        remarksListIds.includes(remark)
      );
      if (
        !compareStringArraysDisregardingOrder(filteredRemarks, defaultValues.remarks) ||
        !compareStringArraysDisregardingOrder(
          currentOtherValues.map((item) => item.remark),
          defaultValues.others.map((item) => item.remark)
        )
      ) {
        reset(
          {
            remarks: filteredRemarks,
            others: currentOtherValues,
          },
          { keepDefaultValues: true }
        );
      }
    }, [remarksListIds]);

    useEffect(() => {
      if (isFirstRef.current) return;

      if (!isSiteSaving && !isSiteUpdatedRef.current) {
        isSiteUpdatedRef.current = true;
      }
    }, [isSiteSaving]);

    const addRemarkOptions = useCallback(
      async (labels: string[]) => {
        const remarkOptions = [];
        for (const label of labels) {
          const existedRemarkOption = dataAllRemarkOptions.find(
            (item: any) =>
              item.type === RemarkOptionType.SPECIFIC &&
              item.translations?.some(
                (t: any) => (t.language === intl.locale || t.language === 'en') && t.label === label
              )
          );
          if (existedRemarkOption) {
            remarkOptions.push(existedRemarkOption);
            continue;
          }
          const jsonData = {
            type: RemarkOptionType.SPECIFIC,
            translations: [
              {
                language: intl.locale,
                label: label,
                description: label,
              },
            ],
          };
          try {
            const { result, status } = await api(
              `admin/v1/create_remark_option`,
              {
                method: 'POST',
                headers: {
                  'content-type': 'application/json',
                },
                body: JSON.stringify(jsonData),
              },
              { token: auth?.accessToken, refresh },
              'site-intelligence'
            ).then((resp) => {
              return { result: resp?.json(), status: resp?.status };
            });
            const resolvedResult = await result;
            if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false) {
              remarkOptions.push(resolvedResult.results);
              setToast({
                show: true,
                type: 'Success',
                header: intl.formatMessage({ id: 'MESSAGE.SUCCESSFUL' }),
                message: intl.formatMessage({ id: 'MESSAGE.UPDATE_SUCCESS' }),
              });
              setErrorMessage('');
              refetchList();
            } else {
              setErrorMessage(resolvedResult.message);
            }
          } catch (err) {
            setToast({
              show: true,
              type: 'Danger',
              header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
              message: 'Fail to add remark options',
            });
            setErrorMessage(intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' }));
          }
        }
        return remarkOptions;
      },
      [dataAllRemarkOptions, intl.locale, auth?.accessToken, setToast, reset]
    );

    const addCaptures = useCallback(
      async (captures: (string | RemarkOptionCapturesValue)[]) => {
        const captureIds: string[] = [];
        for (const capture of captures) {
          // did not edit image
          if (typeof capture === 'string') {
            captureIds.push(capture);
            continue;
          }
          // exist image
          const currentCaptureId = capture.capture_id;
          if (currentCaptureId) {
            captureIds.push(currentCaptureId);
            continue;
          }
          // new image
          const inputCapture = capture.capture;
          const newCaptureFile =
            inputCapture instanceof File
              ? inputCapture
              : typeof inputCapture === 'string'
              ? await urlToFile(inputCapture)
              : null;
          if (newCaptureFile === null) continue;
          const newCaptureId = await saveCapture({
            file: newCaptureFile,
            requestId: requestData.requestId,
            queueId: queueIdInSavingRemarkOptionCaptures,
            url: capture.url,
          });
          if (newCaptureId) {
            captureIds.push(newCaptureId);
          }
        }
        return captureIds;
      },
      [dataAllRemarkOptions, intl.locale, auth?.accessToken, setToast, reset]
    );

    const buildRemarkOptionWithCaptures = (
      remarkOptionCapturesDataMap: RemarkOptionWithCaptures[],
      remarkOptionId: string,
      index: number
    ): TempRequestRemarkOption => {
      const existed = remarkOptionIdToRequestRemarkOptionMap.get(remarkOptionId);

      return {
        ...(existed && { id: existed.id }),
        remarkOptionId: remarkOptionId,
        captures:
          index !== -1 ? remarkOptionCapturesDataMap[index].value : existed?.captureIds ?? [],
      };
    };

    const getCaptureIds = (remarkOptionId: string) =>
      remarkOptionIdToRequestRemarkOptionMap.get(remarkOptionId)?.captureIds;

    const getCurrentCaptures = async (
      capturesDataMap: RemarkOptionWithCaptures[],
      optionType: MapOptionType,
      optionValue: string,
      captureIds: any[],
      isForView: boolean,
      skipToFileWhenCaptureIdExists: boolean
    ) => {
      const fetchData = () =>
        getRemarkOptionCaptures(
          optionType,
          optionValue,
          captureIds,
          isForView,
          skipToFileWhenCaptureIdExists
        );

      // View mode: directly request and return, do not read/write cache
      if (isForView) {
        return await fetchData();
      }

      // Edit mode: read cache first
      const cached = capturesDataMap?.find(
        (c) => c.key.optionType === optionType && c.key.optionValue === optionValue
      );
      if (cached) return cached;

      // No cache, request new data
      const remarkOptionCaptures = await fetchData();

      // Write cache based on conditions
      if (!skipToFileWhenCaptureIdExists) {
        setRemarkOptionCapturesDataMap((prev) => [...prev, remarkOptionCaptures]);
      }

      return remarkOptionCaptures;
    };

    const pushLabelIndex = (indices: number[], label: string) => {
      const index = currentOthers.findIndex((item) => item.remark === label);
      if (index !== -1) {
        indices.push(index);
      }
    };

    const onSubmit: SubmitHandler<FormDataType> = async (formData) => {
      setErrorMessage('');
      setIsLoading(true);
      const remarkIds = formData.remarks;
      const newRemarkOptionCapturesDataMap = [...remarkOptionCapturesDataMap];
      const allOtherRemarkLabelsSet = new Set<string>();
      const indicesToRemove: number[] = [];

      for (const item of formData.others) {
        const remarkLabel = item.remark;
        if (!remarkLabel) continue;

        // Handle non-General Remark cases (early continue to flatten the nesting)
        if (!allGeneralRemarkLabelsMap.has(remarkLabel)) {
          if (!allOtherRemarkLabelsSet.has(remarkLabel)) {
            allOtherRemarkLabelsSet.add(remarkLabel);
          } else {
            pushLabelIndex(indicesToRemove, remarkLabel); // Remove duplicate items
          }
          continue; // Proceed to the next iteration
        }

        // Handle General Remark cases
        const remarkId = allGeneralRemarkLabelsMap.get(remarkLabel)!;

        // 1. Get ID Captures
        const idCaptures = await getCurrentCaptures(
          newRemarkOptionCapturesDataMap,
          MapOptionType.EXISTED,
          remarkId,
          getCaptureIds(remarkId),
          false,
          true
        );

        // 2. Get Label Captures
        let labelCaptures;
        const labelRemarkOptionId = getSpecificRemarkOptionByLabel(remarkLabel)?.remark_option_id;

        if (labelRemarkOptionId) {
          labelCaptures = await getCurrentCaptures(
            newRemarkOptionCapturesDataMap,
            MapOptionType.EXISTED,
            labelRemarkOptionId,
            getCaptureIds(labelRemarkOptionId),
            false,
            true
          );
        } else {
          labelCaptures = await getCurrentCaptures(
            newRemarkOptionCapturesDataMap,
            MapOptionType.NEW,
            remarkLabel,
            undefined,
            false,
            true
          );
        }

        // 3. Merge Captures
        const mergedCaptures = [...idCaptures.value, ...labelCaptures.value];

        // 4. Update cache array (newRemarkOptionCapturesDataMap)
        const existingCacheEntry = newRemarkOptionCapturesDataMap?.find(
          (c) => c.key.optionType === MapOptionType.EXISTED && c.key.optionValue === remarkId
        );

        if (existingCacheEntry) {
          existingCacheEntry.value = mergedCaptures;
        } else {
          newRemarkOptionCapturesDataMap.push({
            key: { optionType: MapOptionType.EXISTED, optionValue: remarkId },
            value: mergedCaptures,
          });
        }

        // 5. Update form state and remove item
        pushLabelIndex(indicesToRemove, remarkLabel);

        if (!remarkIds.includes(remarkId)) {
          remarkIds.push(remarkId);
        }
      }

      setRemarkOptionCapturesDataMap(newRemarkOptionCapturesDataMap);
      setValue('remarks', remarkIds);
      remove(indicesToRemove);

      const otherRemarkLabels: string[] = Array.from(allOtherRemarkLabelsSet);

      const tempRequestRemarkOptions: TempRequestRemarkOption[] = remarkIds.map(
        (remarkOptionId) => {
          const index = newRemarkOptionCapturesDataMap.findIndex(
            (item) =>
              item.key.optionType === MapOptionType.EXISTED &&
              item.key.optionValue === remarkOptionId
          );
          return buildRemarkOptionWithCaptures(
            newRemarkOptionCapturesDataMap,
            remarkOptionId,
            index
          );
        }
      );

      if (otherRemarkLabels.length > 0) {
        const otherRemarkOptions = await addRemarkOptions(otherRemarkLabels);

        tempRequestRemarkOptions.push(
          ...otherRemarkOptions.map((option) => {
            const remarkOptionId = option.remark_option_id;
            const index = newRemarkOptionCapturesDataMap.findIndex(
              (item) =>
                (item.key.optionType === MapOptionType.EXISTED &&
                  item.key.optionValue === remarkOptionId) ||
                (item.key.optionType === MapOptionType.NEW &&
                  item.key.optionValue === getRemarkLabel(option))
            );
            return buildRemarkOptionWithCaptures(
              newRemarkOptionCapturesDataMap,
              remarkOptionId,
              index
            );
          })
        );
      }

      const requestRemarkOptions: {
        _id?: string;
        remark_option_id: string;
        capture_ids: string[];
      }[] = [];
      for (const tempRequestRemarkOption of tempRequestRemarkOptions) {
        const captures = tempRequestRemarkOption.captures;
        const captureIds = await addCaptures(captures);
        requestRemarkOptions.push({
          _id: tempRequestRemarkOption.id,
          remark_option_id: tempRequestRemarkOption.remarkOptionId,
          capture_ids: captureIds,
        });
      }

      const jsonData = {
        request_id: requestData.requestId,
        remark_options: requestRemarkOptions,
        version: requestData.version,
      };
      try {
        const { result, status } = await api(
          `admin/v1/update_site_intelligence_request_remark_options`,
          {
            method: 'POST',
            headers: {
              'content-type': 'application/json',
            },
            body: JSON.stringify(jsonData),
          },
          { token: auth?.accessToken, refresh },
          'site-intelligence'
        ).then((resp) => {
          return { result: resp?.json(), status: resp?.status };
        });
        const resolvedResult = await result;
        if (status === 200 && resolvedResult.code === 200 && resolvedResult.error === false) {
          setToast({
            show: true,
            type: 'Success',
            header: intl.formatMessage({ id: 'MESSAGE.SUCCESSFUL' }),
            message: intl.formatMessage({ id: 'MESSAGE.UPDATE_SUCCESS' }),
          });
        } else {
          setErrorMessage((prev) =>
            prev ? `${prev}. ${resolvedResult.message}` : resolvedResult.message
          );
        }
      } catch (err) {
        setToast({
          show: true,
          type: 'Danger',
          header: intl.formatMessage({ id: 'MESSAGE.ERROR' }),
          message: 'Fail to update remarks',
        });
        setErrorMessage((prev) =>
          prev
            ? `${prev}. ${intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' })}`
            : intl.formatMessage({ id: 'MESSAGE.AN_ERROR_OCCURRED' })
        );
      } finally {
        setRemarkOptionCapturesDataMap([]);
        setIsLoading(false);
      }
    };

    useImperativeHandle(ref, () => ({
      submit: async () => await handleSubmit(onSubmit)(),
      reset: () => {
        reset(currentDefaultValues);
        setRemarkOptionCapturesDataMap([]);
      },
    }));

    const handleOpenModal = async (
      remarkOptionLabel: string,
      remarkOptionId: string,
      remarkOptionCaptureIds: string[],
      isForView = false
    ) => {
      const remarkOptionCaptures = await getCurrentCaptures(
        remarkOptionCapturesDataMap,
        remarkOptionId ? MapOptionType.EXISTED : MapOptionType.NEW,
        remarkOptionId ?? remarkOptionLabel,
        remarkOptionCaptureIds,
        isForView,
        false
      );
      if (isForView) {
        setShowImageModal(true);
        setSelectedImagesData({
          label: remarkOptionLabel,
          captures: remarkOptionCaptures.value.map((c) => ({
            url: c.url,
            image: c.capture,
          })),
        });
      } else {
        setShowAddRemarkOptionCapturesModal(true);
        setSelectedRemarkOptionCaptures({
          label: remarkOptionLabel,
          ...remarkOptionCaptures,
        });
      }
    };

    const renderImageViewComponent = (
      remarkOptionLabel: string,
      remarkOptionId: string,
      remarkOptionCaptureIds: string[]
    ) => {
      return (
        <div className='overlay d-inline-block'>
          <div className='overlay-wrapper'>
            <i className='fas fa-image text-primary fs-3'></i>
          </div>
          <div
            className='overlay-layer bg-dark bg-opacity-50 rounded-1 cursor-pointer'
            onClick={() =>
              handleOpenModal(remarkOptionLabel, remarkOptionId, remarkOptionCaptureIds, true)
            }
            title='View image'
          >
            <i className='fas fa-eye text-white fs-3'></i>
          </div>
        </div>
      );
    };

    const renderImageEditComponent = (
      remarkOptionLabel: string,
      remarkOptionId: string,
      remarkOptionCaptureIds: string[]
    ) => (
      <div>
        {remarkOptionLabel ? (
          <button
            type='button'
            className='btn btn-sm btn-light-primary fw-bold p-0 px-2'
            onClick={() =>
              handleOpenModal(remarkOptionLabel, remarkOptionId, remarkOptionCaptureIds)
            }
          >
            <div className='d-flex align-items-center'>
              <i className='fas fa-pencil-alt'></i>
              Image
            </div>
          </button>
        ) : (
          <button type='button' className='btn btn-sm btn-light-primary fw-bold p-0 px-2 invisible'>
            <div className='d-flex align-items-center'>
              <i className='fas fa-pencil-alt'></i>
              Image
            </div>
          </button>
        )}
      </div>
    );

    return (
      <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ul
            className='list-unstyled mb-6'
            style={{
              columnCount: 2,
              columnWidth: '100px',
              columnGap: '1rem',
            }}
          >
            {remarksList.map((item) => {
              const remarkCaptureIds = getCaptureIds(item.remark_option_id);
              const thumbnailSize = '1rem';
              return (
                <li
                  className='d-flex mb-3 align-items-start'
                  key={item.remark_option_id}
                  style={{ breakInside: 'avoid' }}
                >
                  <div className='d-flex gap-2'>
                    <div className='d-flex'>
                      <div className='form-check'>
                        <Controller
                          name='remarks'
                          control={control}
                          render={({ field, fieldState }) => {
                            return (
                              <input
                                id={item.remark_option_id}
                                value=''
                                type='checkbox'
                                checked={field.value.includes(item.remark_option_id)}
                                // defaultChecked={defaultValues.alias_search}
                                disabled={isLoading}
                                className={clsx(
                                  'form-check-input'
                                  // fieldState.invalid && 'border border-danger',
                                )}
                                onChange={(e) => {
                                  if (field.value.includes(item.remark_option_id)) {
                                    field.onChange([
                                      ...field.value.filter((i) => i !== item.remark_option_id),
                                    ]);
                                  } else {
                                    field.onChange([...field.value, item.remark_option_id]);
                                  }
                                }}
                              />
                            );
                          }}
                        />
                      </div>
                      <label className='form-check-label' htmlFor={item.remark_option_id}>
                        {getRemarkLabel(item)}
                      </label>
                    </div>
                    {remarkCaptureIds?.length > 0 && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        style={{
                          height: thumbnailSize,
                          width: thumbnailSize,
                        }}
                      >
                        {renderImageViewComponent(
                          getRemarkLabel(item),
                          item.remark_option_id,
                          remarkCaptureIds
                        )}
                      </div>
                    )}
                    {currentRemarks.includes(item.remark_option_id) &&
                      renderImageEditComponent(
                        getRemarkLabel(item),
                        item.remark_option_id,
                        remarkCaptureIds
                      )}
                    {getRemarkOptionReviewerNames(item.remark_option_id).length > 0 && (
                      <div className='d-flex align-items-center gap-1 flex-wrap'>
                        {getRemarkOptionReviewerNames(item.remark_option_id).map((reviewerName) => (
                          <Avatar key={reviewerName} name={reviewerName} />
                        ))}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <label className='form-label'>Others</label>
          <div className='w-100 mb-2'>
            {fields.map((field, index) => {
              const currentRemark = currentOthers[index]?.remark;
              const remarkOptionId =
                getSpecificRemarkOptionByLabel(currentRemark)?.remark_option_id ??
                getGeneralRemarkOptionByLabel(currentRemark)?.remark_option_id;
              const remarkCaptureIds = remarkOptionId ? getCaptureIds(remarkOptionId) : [];
              const reviewers = remarkOptionId ? getRemarkOptionReviewerNames(remarkOptionId) : [];
              const thumbnailSize = '1rem';
              return (
                <div className='d-inline-block w-100' key={field.id}>
                  <div className='d-flex justify-content-between align-items-center gap-2 mb-1'>
                    <div className='mx-1 w-100'>
                      <Controller
                        name={`others.${index}.remark`}
                        control={control}
                        render={({ field, fieldState }) => {
                          return (
                            <>
                              <input
                                {...field}
                                list='other-remarks'
                                autoComplete='off'
                                type='text'
                                className={clsx(
                                  'form-control fw-normal fs-base',
                                  fieldState.invalid && 'border border-danger'
                                )}
                                placeholder='e.g. Invalid merchant site'
                              />
                              <datalist id='other-remarks'>
                                {otherRemarksList
                                  .filter((item) => getRemarkLabel(item))
                                  .map((remark, index) => (
                                    <option key={index}>{getRemarkLabel(remark)}</option>
                                  ))}
                              </datalist>
                            </>
                          );
                        }}
                      />
                    </div>
                    <i
                      className={clsx('bi bi-dash-square-fill fs-1 text-danger cursor-pointer')}
                      onClick={() => {
                        if (fields.length > 1) {
                          remove(index);
                        } else {
                          setValue('others.0.remark', '', { shouldDirty: true });
                        }
                      }}
                    ></i>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      style={{
                        height: thumbnailSize,
                        width: thumbnailSize,
                      }}
                    >
                      {remarkCaptureIds?.length > 0 ? (
                        renderImageViewComponent(currentRemark, remarkOptionId, remarkCaptureIds)
                      ) : (
                        <div className='overlay d-inline-block'>
                          <div className='overlay-wrapper'>
                            <i className='fas fa-image text-gray-500 fs-3 invisible'></i>
                          </div>
                        </div>
                      )}
                    </div>
                    {currentRemark
                      ? renderImageEditComponent(currentRemark, remarkOptionId, remarkCaptureIds)
                      : renderImageEditComponent('', undefined, remarkCaptureIds)}
                    <div className='d-flex align-items-center gap-1 flex-wrap'>
                      {reviewers.length > 0 ? (
                        reviewers.map((reviewerName) => (
                          <Avatar key={reviewerName} name={reviewerName} />
                        ))
                      ) : (
                        <Avatar name={''} disableNameTooltip className='invisible' />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className='mx-2'>
            <i
              className='bi bi-plus-square-fill fs-1 text-success cursor-pointer'
              onClick={() => {
                append({ remark: '' });
              }}
            ></i>
          </div>

          {errorMessage && (
            <div className='text-danger my-4 border border-danger rounded p-2'>{errorMessage}</div>
          )}
        </form>

        {showAddRemarkOptionCapturesModal && (
          <AddRemarkOptionCapturesModal
            data={selectedRemarkOptionCaptures}
            show={showAddRemarkOptionCapturesModal}
            handleClose={() => setShowAddRemarkOptionCapturesModal(false)}
            setRemarkOptionCapturesDataMap={setRemarkOptionCapturesDataMap}
          />
        )}
      </>
    );
  }
);

export { EditSiRemarks };
